import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchOverview, fetchPolls } from "@/lib/api";
import PartyCard from "@/components/PartyCard";
import Seo from "@/components/Seo";
import { ArrowRight, BarChart3, Scale, TrendingDown, ShieldAlert, Flame } from "lucide-react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
} from "recharts";

export default function Home() {
    const [overview, setOverview] = useState([]);
    const [polls, setPolls] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([fetchOverview(), fetchPolls()])
            .then(([ov, p]) => {
                setOverview(ov);
                setPolls(p);
            })
            .finally(() => setLoading(false));
    }, []);

    const totalPromises = overview.reduce((a, b) => a + b.promises_total, 0);
    const totalBrudt = overview.reduce((a, b) => a + b.brudt, 0);
    const totalLies = overview.reduce((a, b) => a + b.lies_count, 0);
    const totalValgflaesk = overview.reduce((a, b) => a + b.valgflaesk_count, 0);
    const totalScandals = overview.reduce((a, b) => a + (b.scandals_count || 0), 0);

    return (
        <div data-testid="home-page">
            <Seo
                title="Holder danske politikere deres valgløfter?"
                description="Løftebarometeret — uafhængigt dansk tillidsmål. Tillidsscore, dokumenterede løftebrud, valgflæsk og faktatjek for alle 10 partier i Folketinget. Tag vælger-quizzen og sammenlign partier."
                keywords="valgløfter, løftebrud, valgflæsk, danske politikere, Folketinget, politisk tillid, Socialdemokratiet, Venstre, Moderaterne, SF, Enhedslisten, vælgerquiz"
                path="/"
                structuredData={{
                    "@context": "https://schema.org",
                    "@type": "WebPage",
                    "name": "Løftebarometeret — holder danske politikere deres valgløfter?",
                    "inLanguage": "da-DK",
                    "about": {
                        "@type": "Thing",
                        "name": "Dansk politik og valgløfter",
                    },
                }}
            />
            {/* HERO */}
            <section className="bg-royal-gradient text-white relative overflow-hidden grain">
                <div className="absolute inset-0 bg-grid opacity-20" />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative">
                    <div className="grid lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7 animate-fade-up">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs uppercase tracking-[0.18em] mb-6">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#BB1B2D]" />
                                Uafhængigt dansk tillidsmål
                            </div>
                            <h1 className="font-serif-dm text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                                Holder de <span className="text-[#F4C96B] italic">virkelig</span><br />
                                hvad de lover?
                            </h1>
                            <p className="mt-6 text-lg text-white/80 max-w-2xl leading-relaxed">
                                Løftebarometeret viser dig, hvordan danske partier forvalter
                                deres valgløfter. Se tillidsscorer, dokumenterede løftebrud
                                og hvor ofte der serveres det rene valgflæsk.
                            </p>
                            <div className="mt-8 flex flex-wrap gap-3">
                                <Link
                                    to="/quiz"
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-white text-royal rounded-full font-semibold hover:bg-[#F4C96B] transition-colors"
                                    data-testid="cta-quiz"
                                >
                                    Tag vælger-quizzen <ArrowRight className="w-4 h-4" />
                                </Link>
                                <Link
                                    to="/statistik"
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/25 rounded-full font-semibold hover:bg-white/20 transition-colors"
                                    data-testid="cta-stats"
                                >
                                    Se tillidsgrafen
                                </Link>
                                <Link
                                    to="/sammenlign"
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/25 rounded-full font-semibold hover:bg-white/20 transition-colors"
                                    data-testid="cta-compare"
                                >
                                    Sammenlign partier
                                </Link>
                            </div>
                        </div>

                        <div className="lg:col-span-5">
                            <div className="bg-white/5 border border-white/15 backdrop-blur-sm rounded-2xl p-6">
                                <div className="flex items-baseline justify-between mb-4">
                                    <span className="text-white/70 uppercase text-xs tracking-wider">
                                        Samlet billede
                                    </span>
                                    <span className="text-xs text-white/50">live data</span>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <Stat label="Skandaler" value={totalScandals} icon={Flame} accent />
                                    <Stat label="Brudte løfter" value={totalBrudt} icon={TrendingDown} accent />
                                    <Stat label="Dokumenterede løgne" value={totalLies} icon={ShieldAlert} accent />
                                    <Stat label="Valgflæsk-tilfælde" value={totalValgflaesk} icon={BarChart3} accent />
                                    <Stat label="Løfter registreret" value={totalPromises} icon={Scale} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FEATURED PARTIES */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
                    <div>
                        <span className="text-xs uppercase tracking-[0.2em] text-[#4F5A7A] font-semibold">
                            Partioverblik
                        </span>
                        <h2 className="font-serif-dm text-4xl md:text-5xl text-royal mt-2 leading-tight">
                            De 6 mest målte partier
                        </h2>
                    </div>
                    <Link
                        to="/partier"
                        className="text-royal font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all"
                        data-testid="home-all-parties"
                    >
                        Se alle partier <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {overview.slice(0, 6).map((o) => (
                        <PartyCard
                            key={o.party_id}
                            party={{
                                id: o.party_id,
                                name: o.party_name,
                                short_name: o.short_name,
                                color: o.color,
                                leader: "",
                                ideology: "",
                                seats: "–",
                            }}
                            stats={o}
                        />
                    ))}
                </div>
            </section>

            {/* TRUST COMPARISON CHART */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
                    <div>
                        <span className="text-xs uppercase tracking-[0.2em] text-[#4F5A7A] font-semibold">
                            Tillidsgraf
                        </span>
                        <h2 className="font-serif-dm text-4xl md:text-5xl text-royal mt-2 leading-tight">
                            Hvem holder deres ord?
                        </h2>
                        <p className="text-[#4F5A7A] mt-3 max-w-2xl">
                            Tillidsscoren er beregnet som andelen af løfter, der er holdt
                            (fuldt eller delvist) ud af samtlige registrerede løfter.
                        </p>
                    </div>
                    <Link
                        to="/statistik"
                        className="text-royal font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all"
                        data-testid="home-stats-link"
                    >
                        Dybere statistik <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>

                <div className="bg-white border border-[#E5E8F0] rounded-2xl p-4 md:p-8">
                    {!loading && overview.length > 0 && (
                        <ResponsiveContainer width="100%" height={420}>
                            <BarChart data={overview} margin={{ top: 20, right: 20, left: 0, bottom: 40 }}>
                                <CartesianGrid stroke="#EEF0F7" vertical={false} />
                                <XAxis
                                    dataKey="short_name"
                                    tick={{ fill: "#4F5A7A", fontSize: 12, fontWeight: 600 }}
                                    axisLine={{ stroke: "#D1D6E4" }}
                                />
                                <YAxis
                                    tick={{ fill: "#4F5A7A", fontSize: 12 }}
                                    axisLine={false}
                                    tickLine={false}
                                    domain={[0, 100]}
                                    unit="%"
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: "#fff",
                                        border: "1px solid #E5E8F0",
                                        borderRadius: "10px",
                                        fontFamily: "Manrope",
                                    }}
                                    formatter={(value) => [`${value}%`, "Tillidsscore"]}
                                    labelFormatter={(label, payload) =>
                                        payload?.[0]?.payload?.party_name || label
                                    }
                                />
                                <Bar dataKey="trust_score" radius={[8, 8, 0, 0]}>
                                    {overview.map((entry) => (
                                        <Bar key={entry.party_id} fill={entry.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </section>

            {/* FEATURED POLL */}
            {polls[0] && (
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
                    <div className="bg-royal text-white rounded-3xl p-8 md:p-12 relative overflow-hidden">
                        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#BB1B2D]/20 blur-3xl" />
                        <div className="relative">
                            <span className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold">
                                Aktuel afstemning
                            </span>
                            <h3 className="font-serif-dm text-3xl md:text-4xl mt-2 mb-4 max-w-3xl">
                                {polls[0].question}
                            </h3>
                            <p className="text-white/75 max-w-2xl mb-6">{polls[0].description}</p>
                            <Link
                                to="/afstemninger"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-royal rounded-full font-semibold hover:bg-[#F4C96B] transition-colors"
                                data-testid="home-poll-cta"
                            >
                                Afgiv din stemme <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}

function Stat({ label, value, icon: Icon, accent }) {
    return (
        <div className={`rounded-xl p-4 ${accent ? "bg-[#BB1B2D]/15" : "bg-white/10"}`}>
            <Icon className={`w-4 h-4 mb-2 ${accent ? "text-[#FFD6DA]" : "text-white/60"}`} />
            <div className="font-serif-dm text-3xl text-white">{value}</div>
            <div className="text-xs text-white/70 mt-1">{label}</div>
        </div>
    );
}
